import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import {
  Users,
  Plus,
  Search,
  Edit2,
  Trash2,
  Shield,
  ShieldCheck,
  Crown,
  X,
  Eye,
  EyeOff,
} from "lucide-react";
import AdminLayout from "../components/AdminLayout";
import { usersApi } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const ROLE_OPTIONS = [
  { value: "superadmin", label: "Superadmin", icon: Crown, color: "text-amber-600 bg-amber-100" },
  { value: "admin", label: "Admin", icon: ShieldCheck, color: "text-emerald-600 bg-emerald-100" },
  { value: "owner", label: "Owner", icon: Shield, color: "text-purple-600 bg-purple-100" },
];

const RoleBadge = ({ role }) => {
  const opt = ROLE_OPTIONS.find((r) => r.value === role) || ROLE_OPTIONS[1];
  const Icon = opt.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${opt.color}`}>
      <Icon className="w-3.5 h-3.5" />
      {opt.label}
    </span>
  );
};

const StatusBadge = ({ isActive }) => (
  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
    isActive ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"
  }`}>
    {isActive ? "Aktif" : "Nonaktif"}
  </span>
);

const AdminUsersPage = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterRole, setFilterRole] = useState("");
  const [page, setPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    role: "admin",
    phone: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (search) params.search = search;
      if (filterRole) params.role = filterRole;
      const data = await usersApi.getAll(params);
      setUsers(data.users);
      setTotal(data.total);
    } catch (err) {
      toast.error("Gagal memuat data user");
    } finally {
      setLoading(false);
    }
  }, [page, search, filterRole]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const openCreateModal = () => {
    setEditingUser(null);
    setFormData({ name: "", username: "", email: "", password: "", role: "admin", phone: "" });
    setShowPassword(false);
    setShowModal(true);
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      username: user.username || "",
      email: user.email,
      password: "",
      role: user.role,
      phone: user.phone || "",
    });
    setShowPassword(false);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editingUser) {
        const updateData = { ...formData };
        if (!updateData.password) delete updateData.password;
        await usersApi.update(editingUser.id, updateData);
        toast.success("User berhasil diperbarui");
      } else {
        if (!formData.password) {
          toast.error("Password wajib diisi");
          setSubmitting(false);
          return;
        }
        await usersApi.create(formData);
        toast.success("User berhasil dibuat");
      }
      setShowModal(false);
      fetchUsers();
    } catch (err) {
      toast.error(err.message || "Gagal menyimpan user");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (user) => {
    if (user.id === currentUser?.id) {
      toast.error("Tidak bisa menghapus akun sendiri");
      return;
    }
    if (!window.confirm(`Hapus user "${user.name}"?`)) return;
    try {
      await usersApi.delete(user.id);
      toast.success("User berhasil dihapus");
      fetchUsers();
    } catch (err) {
      toast.error(err.message || "Gagal menghapus user");
    }
  };

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6 lg:p-8" data-testid="admin-users-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-stone-900 flex items-center gap-2">
              <Users className="w-7 h-7 text-emerald-600" />
              Manajemen User
            </h1>
            <p className="text-stone-500 mt-1">Kelola pengguna dan hak akses sistem</p>
          </div>
          <button
            onClick={openCreateModal}
            className="inline-flex items-center gap-2 px-4 h-12 bg-emerald-600 text-white rounded-xl font-medium hover:bg-emerald-700 transition-colors"
            data-testid="btn-create-user"
          >
            <Plus className="w-5 h-5" />
            Tambah User
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
            <input
              type="text"
              placeholder="Cari nama atau email..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="w-full h-12 pl-10 pr-4 border border-stone-200 rounded-xl text-base focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              data-testid="search-users"
            />
          </div>
          <select
            value={filterRole}
            onChange={(e) => { setFilterRole(e.target.value); setPage(1); }}
            className="h-12 px-4 border border-stone-200 rounded-xl text-base focus:ring-2 focus:ring-emerald-500"
            data-testid="filter-role"
          >
            <option value="">Semua Role</option>
            {ROLE_OPTIONS.map((r) => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
        </div>

        {/* Table (desktop) / Cards (mobile) */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-12 text-stone-500">
            <Users className="w-12 h-12 mx-auto text-stone-300 mb-3" />
            <p>Belum ada user ditemukan</p>
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden md:block bg-white rounded-2xl border border-stone-100 shadow-sm overflow-hidden">
              <table className="w-full">
                <thead className="bg-stone-50 border-b border-stone-100">
                  <tr>
                    <th className="text-left px-6 py-3 text-sm font-medium text-stone-500">Nama</th>
                    <th className="text-left px-6 py-3 text-sm font-medium text-stone-500">Email</th>
                    <th className="text-left px-6 py-3 text-sm font-medium text-stone-500">Role</th>
                    <th className="text-left px-6 py-3 text-sm font-medium text-stone-500">Status</th>
                    <th className="text-right px-6 py-3 text-sm font-medium text-stone-500">Aksi</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {users.map((u) => (
                    <tr key={u.id} className="hover:bg-stone-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="font-medium text-stone-900">{u.name}</div>
                        {u.phone && <div className="text-sm text-stone-500">{u.phone}</div>}
                      </td>
                      <td className="px-6 py-4 text-stone-600">{u.email}</td>
                      <td className="px-6 py-4"><RoleBadge role={u.role} /></td>
                      <td className="px-6 py-4"><StatusBadge isActive={u.is_active} /></td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => openEditModal(u)}
                            className="p-2 rounded-lg text-stone-500 hover:text-emerald-600 hover:bg-emerald-50 transition-colors"
                            data-testid={`edit-user-${u.id}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          {u.id !== currentUser?.id && (
                            <button
                              onClick={() => handleDelete(u)}
                              className="p-2 rounded-lg text-stone-500 hover:text-rose-600 hover:bg-rose-50 transition-colors"
                              data-testid={`delete-user-${u.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden space-y-3">
              {users.map((u) => (
                <div key={u.id} className="bg-white rounded-xl border border-stone-100 p-4 shadow-sm">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-medium text-stone-900">{u.name}</p>
                      <p className="text-sm text-stone-500">{u.email}</p>
                      {u.phone && <p className="text-sm text-stone-500">{u.phone}</p>}
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => openEditModal(u)} className="p-2 text-stone-400 hover:text-emerald-600">
                        <Edit2 className="w-4 h-4" />
                      </button>
                      {u.id !== currentUser?.id && (
                        <button onClick={() => handleDelete(u)} className="p-2 text-stone-400 hover:text-rose-600">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-3">
                    <RoleBadge role={u.role} />
                    <StatusBadge isActive={u.is_active} />
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {total > 20 && (
              <div className="flex justify-center gap-2 mt-6">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-4 py-2 rounded-lg border border-stone-200 text-sm disabled:opacity-50"
                >
                  Sebelumnya
                </button>
                <span className="px-4 py-2 text-sm text-stone-600">
                  Hal {page} dari {Math.ceil(total / 20)}
                </span>
                <button
                  onClick={() => setPage((p) => p + 1)}
                  disabled={page >= Math.ceil(total / 20)}
                  className="px-4 py-2 rounded-lg border border-stone-200 text-sm disabled:opacity-50"
                >
                  Selanjutnya
                </button>
              </div>
            )}
          </>
        )}

        {/* Modal Create/Edit */}
        {showModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-stone-600/50">
            <div className="bg-white rounded-2xl w-full max-w-md shadow-xl max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-6 border-b border-stone-100">
                <h2 className="text-lg font-semibold text-stone-900">
                  {editingUser ? "Edit User" : "Tambah User Baru"}
                </h2>
                <button onClick={() => setShowModal(false)} className="p-1 rounded-lg hover:bg-stone-100">
                  <X className="w-5 h-5 text-stone-500" />
                </button>
              </div>
              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">Nama</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full h-12 px-4 border border-stone-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    data-testid="input-user-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">Username</label>
                  <input
                    type="text"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    className="w-full h-12 px-4 border border-stone-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="username (opsional)"
                    data-testid="input-user-username"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">Email</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full h-12 px-4 border border-stone-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    data-testid="input-user-email"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">
                    Password {editingUser && <span className="text-stone-400">(kosongkan jika tidak diubah)</span>}
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      required={!editingUser}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="w-full h-12 px-4 pr-12 border border-stone-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      data-testid="input-user-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">Role</label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    className="w-full h-12 px-4 border border-stone-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    data-testid="input-user-role"
                  >
                    {ROLE_OPTIONS.map((r) => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">No. Telepon</label>
                  <input
                    type="text"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full h-12 px-4 border border-stone-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="08xxxxxxxxxx"
                    data-testid="input-user-phone"
                  />
                </div>
                {editingUser && (
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-1">Status</label>
                    <select
                      value={formData.is_active !== undefined ? formData.is_active : editingUser.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.value === "true" })}
                      className="w-full h-12 px-4 border border-stone-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    >
                      <option value="true">Aktif</option>
                      <option value="false">Nonaktif</option>
                    </select>
                  </div>
                )}
                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="flex-1 h-12 border border-stone-200 rounded-xl font-medium text-stone-700 hover:bg-stone-50 transition-colors"
                  >
                    Batal
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 h-12 bg-emerald-600 text-white rounded-xl font-medium hover:bg-emerald-700 transition-colors disabled:opacity-50"
                    data-testid="btn-submit-user"
                  >
                    {submitting ? "Menyimpan..." : editingUser ? "Simpan" : "Buat User"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminUsersPage;
